import Swal from "sweetalert2";

const ICON_LABELS = {
  success: "Success",
  error: "Error",
  warning: "Warning",
  info: "Info",
  question: "Confirm",
};

const SVG_ICONS = {
  success: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" fill="rgba(16, 185, 129, 0.08)"></circle><polyline points="9 11 12 14 17 9"></polyline></svg>`,
  error: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" fill="rgba(239, 68, 68, 0.08)"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`,
  warning: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" fill="rgba(245, 158, 11, 0.08)"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>`,
  info: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" fill="rgba(59, 130, 246, 0.08)"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>`,
  question: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" fill="rgba(139, 92, 246, 0.08)"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>`,
};

const normalizeType = (type = "info") => ICON_LABELS[type] ? type : "info";

const escapeHtml = (value) => String(value || "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const buildTitle = (title, type) => {
  if (title) return title;
  return ICON_LABELS[normalizeType(type)];
};

const buildMessage = (title, message, fallback = "") => {
  if (message) return message;
  if (title && ICON_LABELS[String(title).toLowerCase()] === undefined) return "";
  return fallback;
};

const buildAlertContent = (title, message, type) => {
  const icon = normalizeType(type);

  if (icon === "success") {
    return {
      title: undefined,
      message: message || title || "",
    };
  }

  return {
    title: buildTitle(title, icon),
    message: buildMessage(title, message),
  };
};

export const getErrorMessage = (error, fallback = "Something went wrong. Please try again.") => {
  const data = error?.response?.data || error?.data;

  if (typeof data === "string" && data.trim()) return data;
  if (data?.message) return data.message;
  if (data?.title) return data.title;
  if (Array.isArray(data?.errors)) return data.errors.join(" ");
  if (data?.errors && typeof data.errors === "object") {
    const messages = Object.values(data.errors).flat().filter(Boolean);
    if (messages.length) return messages.join(" ");
  }
  if (error?.message) return error.message;

  return fallback;
};

const buildToastClass = (icon, customClass = {}) => ({
  title: "wf-toast-title",
  icon: "wf-toast-icon",
  timerProgressBar: "wf-toast-progress",
  closeButton: "wf-toast-close",
  ...customClass,
  popup: `wf-toast wf-toast-${icon} ${customClass.popup || ""}`.trim(),
});
const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  showCloseButton: true,
  timer: 4200,
  timerProgressBar: true,
  width: "min(420px, calc(100vw - 32px))",
  showClass: {
    popup: "swal2-show wf-toast-show",
  },
  hideClass: {
    popup: "swal2-hide wf-toast-hide",
  },
  customClass: {
    popup: "wf-toast",
    title: "wf-toast-title",
    icon: "wf-toast-icon",
    timerProgressBar: "wf-toast-progress",
    closeButton: "wf-toast-close",
  },
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
  },
});

export const getAlert = (title, message, type = "info", options = {}) => {
  const { html, didOpen, customClass, ...restOptions } = options;
  const icon = normalizeType(type);
  const alertContent = buildAlertContent(title, message, icon);

  return Swal.fire({
    title: alertContent.title,
    [html ? "html" : "text"]: alertContent.message,
    icon,
    buttonsStyling: false,
    confirmButtonText: options.confirmButtonText || "Close",
    showClass: {
      popup: "swal2-show wf-alert-show",
    },
    hideClass: {
      popup: "swal2-hide wf-alert-hide",
    },
    customClass: {
      popup: "wf-alert-popup",
      title: "wf-alert-title",
      htmlContainer: "wf-alert-text",
      confirmButton: "wf-alert-btn wf-alert-btn-primary",
      icon: "wf-alert-icon",
      ...customClass,
    },
    didOpen: (popup) => {
      const confirmButton = Swal.getConfirmButton();
      if (confirmButton && !options.confirmButtonText) {
        confirmButton.innerHTML = `<i class="fa fa-times" aria-hidden="true" style="margin-right: 8px;"></i><span>Close</span>`;
      }
      if (didOpen) didOpen(popup);
    },
    ...restOptions,
  });
};

export const getConfirmAlert = (title, message, type = "question", options = {}) => {
  const { html, didOpen, customClass, ...restOptions } = options;
  const icon = normalizeType(type);

  return Swal.fire({
    title: buildTitle(title, icon),
    [html ? "html" : "text"]: buildMessage(title, message),
    icon,
    showCancelButton: true,
    buttonsStyling: false,
    confirmButtonText: options.confirmButtonText || "Yes",
    cancelButtonText: options.cancelButtonText || "No",
    customClass: {
      popup: "wf-alert-popup",
      title: "wf-alert-title",
      htmlContainer: "wf-alert-text",
      confirmButton: "wf-alert-btn wf-alert-btn-primary",
      cancelButton: "wf-alert-btn wf-alert-btn-secondary",
      icon: "wf-alert-icon",
      ...customClass,
    },
    didOpen: (popup) => {
      const confirmButton = Swal.getConfirmButton();
      if (confirmButton && !options.confirmButtonText) {
        confirmButton.innerHTML = `<i class="fa fa-check" aria-hidden="true" style="margin-right: 8px;"></i><span>Yes</span>`;
      }
      const cancelButton = Swal.getCancelButton();
      if (cancelButton && !options.cancelButtonText) {
        cancelButton.innerHTML = `<i class="fa fa-times" aria-hidden="true" style="margin-right: 8px;"></i><span>No</span>`;
      }
      if (didOpen) didOpen(popup);
    },
    ...restOptions,
  }).then((result) => result);
};

export const getSigningModeChoice = (options = {}) => {
  const {
    title = "Send through eSign",
    message = "Choose how the signers should sign this document.",
    confirmButtonText = "Send",
    cancelButtonText = "Cancel",
    defaultMode = "Parallel",
  } = options;

  return Swal.fire({
    title,
    text: message,
    icon: "question",
    input: "radio",
    inputValue: defaultMode,
    inputOptions: {
      Parallel: "Parallel — all signers can sign at the same time",
      Sequential: "Sequential — signers sign one after another, in order",
    },
    inputValidator: (value) => (!value ? "Please choose a signing mode." : undefined),
    showCancelButton: true,
    buttonsStyling: false,
    confirmButtonText,
    cancelButtonText,
    customClass: {
      popup: "wf-alert-popup",
      title: "wf-alert-title",
      htmlContainer: "wf-alert-text",
      input: "wf-alert-radio",
      confirmButton: "wf-alert-btn wf-alert-btn-primary",
      cancelButton: "wf-alert-btn wf-alert-btn-secondary",
      icon: "wf-alert-icon",
    },
    didOpen: () => {
      const confirmButton = Swal.getConfirmButton();
      if (confirmButton) {
        confirmButton.innerHTML = `<i class="fa fa-paper-plane" aria-hidden="true" style="margin-right: 8px;"></i><span>${confirmButtonText}</span>`;
      }
      const cancelButton = Swal.getCancelButton();
      if (cancelButton) {
        cancelButton.innerHTML = `<i class="fa fa-times" aria-hidden="true" style="margin-right: 8px;"></i><span>${cancelButtonText}</span>`;
      }
    },
  }).then((result) => (result.isConfirmed ? result.value : null));
};

export const getConfirmAlertWithAuth = (title, message, type, options = {}) => {
  const {
    html,
    didOpen,
    customClass,
    authCode = "12345",
    validateAuth,
    inputLabel,
    inputPlaceholder,
    ...restOptions
  } = options;

  return getConfirmAlert(title, message, type, {
    html,
    didOpen,
    customClass,
    ...restOptions,
  }).then(async (result) => {
    if (result.isConfirmed) {
      while (true) {
        const { value: enteredCode, isConfirmed: codeConfirmed } = await Swal.fire({
          title: "Enter Authorization Code",
          input: "password",
          inputLabel: inputLabel || "Required for deleting file",
          inputPlaceholder: inputPlaceholder || "Enter code...",
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: "Verify",
          cancelButtonText: "Cancel",
          customClass: {
            popup: "wf-alert-popup",
            title: "wf-alert-title",
            confirmButton: "wf-alert-btn wf-alert-btn-primary",
            cancelButton: "wf-alert-btn wf-alert-btn-secondary",
          },
        });

        if (!codeConfirmed) return { isConfirmed: false };

        const isValid = validateAuth
          ? await validateAuth(enteredCode)
          : enteredCode === authCode;

        if (isValid) return { isConfirmed: true };

        await getAlert("Incorrect Code", "The authorization code is wrong. Try again.", "error");
      }
    }

    return { isConfirmed: false };
  });
};

export const getToast = (message, type = "success", options = {}) => {
  const icon = normalizeType(type);
  return Toast.fire({
    icon,
    iconHtml: SVG_ICONS[icon] || undefined,
    title: message,
    ...options,
    customClass: buildToastClass(icon, options.customClass),
  });
};

export const getApiErrorToast = (error, options = {}) => {
  const status = error?.response?.status || error?.status;
  const message = getErrorMessage(error);
  const title = status ? `Request failed (${status})` : "Request failed";

  return Toast.fire({
    icon: "error",
    iconHtml: SVG_ICONS.error,
    title,
    html: `<div class="wf-toast-message">${escapeHtml(message)}</div>`,
    timer: options.timer || 6000,
    ...options,
    customClass: buildToastClass("error", options.customClass),
  });
};

export const showLoading = (title = "Loading...", message = "Please wait...", options = {}) => {
  Swal.fire({
    title,
    text: message,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    showConfirmButton: false,
    customClass: {
      popup: "wf-alert-popup wf-alert-loading",
      title: "wf-alert-title",
      htmlContainer: "wf-alert-text",
      loader: "wf-alert-loader",
      ...options.customClass,
    },
    didOpen: () => {
      Swal.showLoading();
    },
    ...options,
  });
};

export const closeLoading = () => {
  Swal.close();
};
